//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { useCallback, useEffect, useRef } from 'react';
import { useActiveLoopContext } from '../ActiveLoopContext';
import { useTeamspaceContext } from '../TeamspaceContext';
import {
  ContextPayload,
  buildContextMessage,
  parseContextMessage,
  sendMessageToIframe,
} from '../utils/contextMessaging';

export interface UseContextBridgeArgs {
  getIframe?: () => HTMLIFrameElement | null;
  allowedOrigins?: string[];
}

export interface UseContextBridge {
  getCurrentPayload: () => ContextPayload | null;
  broadcastContext: () => void;
  requestContext: () => void;
}

export function useContextBridge(
  args: UseContextBridgeArgs = {},
): UseContextBridge {
  const { getIframe, allowedOrigins = [] } = args;

  const {
    activeLoopId,
    activeLoopName,
    activeTeamspaceId,
    setLoopContext,
    setTeamspaceContext,
  } = useActiveLoopContext();

  const {
    teamspaceId: teamspaceCtxId,
    teamspaceName,
    teamspaceList,
    _setTeamspaceId,
    _setTeamspaceName,
    _setTeamspaceSlug,
  } = useTeamspaceContext();

  // Use refs for values accessed inside the message listener to avoid
  // tearing down / re-adding the listener on every context change.
  const activeLoopIdRef = useRef(activeLoopId);
  const activeLoopNameRef = useRef(activeLoopName);
  const activeTeamspaceIdRef = useRef(activeTeamspaceId);
  const teamspaceCtxIdRef = useRef(teamspaceCtxId);
  const teamspaceNameRef = useRef(teamspaceName);
  const setLoopContextRef = useRef(setLoopContext);
  const setTeamspaceContextRef = useRef(setTeamspaceContext);
  const teamspaceListRef = useRef(teamspaceList);
  const teamspaceSettersRef = useRef({
    _setTeamspaceId,
    _setTeamspaceName,
    _setTeamspaceSlug,
  });
  const getIframeRef = useRef(getIframe);
  const allowedOriginsRef = useRef(allowedOrigins);

  useEffect(() => {
    activeLoopIdRef.current = activeLoopId;
  }, [activeLoopId]);
  useEffect(() => {
    activeLoopNameRef.current = activeLoopName;
  }, [activeLoopName]);
  useEffect(() => {
    activeTeamspaceIdRef.current = activeTeamspaceId;
  }, [activeTeamspaceId]);
  useEffect(() => {
    teamspaceCtxIdRef.current = teamspaceCtxId;
  }, [teamspaceCtxId]);
  useEffect(() => {
    teamspaceNameRef.current = teamspaceName;
  }, [teamspaceName]);
  useEffect(() => {
    setLoopContextRef.current = setLoopContext;
  }, [setLoopContext]);
  useEffect(() => {
    setTeamspaceContextRef.current = setTeamspaceContext;
  }, [setTeamspaceContext]);
  useEffect(() => {
    teamspaceListRef.current = teamspaceList;
  }, [teamspaceList]);
  useEffect(() => {
    teamspaceSettersRef.current = {
      _setTeamspaceId,
      _setTeamspaceName,
      _setTeamspaceSlug,
    };
  }, [_setTeamspaceId, _setTeamspaceName, _setTeamspaceSlug]);
  useEffect(() => {
    getIframeRef.current = getIframe;
  }, [getIframe]);
  useEffect(() => {
    allowedOriginsRef.current = allowedOrigins;
  }, [allowedOrigins]);

  const getCurrentPayload = useCallback((): ContextPayload | null => {
    const tsId = activeTeamspaceIdRef.current || teamspaceCtxIdRef.current;
    if (!tsId) return null;
    // The shell's teamspace name belongs to the shell's teamspace. When the tab
    // context points at a different teamspace (e.g. just switched inside the
    // app) the name would be stale, so leave it out; it is optional.
    const nameMatchesId =
      !activeTeamspaceIdRef.current ||
      activeTeamspaceIdRef.current === teamspaceCtxIdRef.current;
    return {
      teamspaceId: tsId,
      teamspaceName: nameMatchesId ? teamspaceNameRef.current : undefined,
      activeTeamspaceId: activeTeamspaceIdRef.current,
      loopId: activeLoopIdRef.current,
      loopName: activeLoopNameRef.current,
      level: activeLoopIdRef.current ? 'loop' : 'teamspace',
    };
  }, []);

  const broadcastContext = useCallback(() => {
    const payload = getCurrentPayload();
    if (!payload) return;
    const iframe = getIframeRef.current?.();
    if (!iframe) return;
    try {
      const message = buildContextMessage('CONTEXT_UPDATE', payload);
      sendMessageToIframe(iframe, message, allowedOriginsRef.current);
    } catch {
      // No valid payload to send — silently ignore.
    }
  }, [getCurrentPayload]);

  const requestContext = useCallback(() => {
    const payload = getCurrentPayload();
    if (!payload) return;
    const iframe = getIframeRef.current?.();
    if (!iframe) return;
    try {
      const message = buildContextMessage('CONTEXT_REQUEST', payload);
      sendMessageToIframe(iframe, message, allowedOriginsRef.current);
    } catch {
      // No valid payload to send — silently ignore.
    }
  }, [getCurrentPayload]);

  // Single message listener — registered once on mount, removed on unmount.
  const listenerAttached = useRef(false);

  useEffect(() => {
    if (listenerAttached.current) return;
    listenerAttached.current = true;

    const handleMessage = (event: MessageEvent) => {
      const msg = parseContextMessage(event, allowedOriginsRef.current);
      if (!msg) return;

      // If getIframe is provided, only accept messages from the owned iframe.
      if (getIframeRef.current) {
        const iframe = getIframeRef.current();
        if (iframe && event.source !== iframe.contentWindow) return;
      }

      if (msg.type === 'CONTEXT_UPDATE') {
        const { teamspaceId, loopId, loopName } = msg.payload;

        // Once the shell knows the user's teamspaces, ignore ids it does not
        // recognise (e.g. an app's internal numeric id). Before the list has
        // loaded there is nothing to validate against, so accept the update.
        const list = teamspaceListRef.current;
        const known = list?.find((ts) => ts.id === teamspaceId);
        if (list && list.length > 0 && !known) return;

        if (loopId) {
          setLoopContextRef.current(loopId, loopName ?? loopId, teamspaceId);
        } else {
          setTeamspaceContextRef.current(teamspaceId);
        }

        // Keep this tab's shell (sidebar, loop lists) on the same teamspace.
        // Per-tab React state only: nothing is written to shared localStorage,
        // so other tabs keep their own teamspace.
        if (known && known.id !== teamspaceCtxIdRef.current) {
          const setters = teamspaceSettersRef.current;
          setters._setTeamspaceId(known.id);
          setters._setTeamspaceName(known.name);
          setters._setTeamspaceSlug(known.slug);
        }
      } else if (msg.type === 'CONTEXT_REQUEST') {
        // Respond with current context.
        const payload = getCurrentPayload();
        if (!payload) return;
        const iframe = getIframeRef.current?.();
        if (!iframe) return;
        try {
          const response = buildContextMessage('CONTEXT_UPDATE', payload);
          sendMessageToIframe(iframe, response, allowedOriginsRef.current);
        } catch {
          // Ignore build errors.
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      listenerAttached.current = false;
      window.removeEventListener('message', handleMessage);
    };
  }, [getCurrentPayload]);

  return { getCurrentPayload, broadcastContext, requestContext };
}
